import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import { strings } from '../../resources/strings';
import ServicesContext from '../../contexts/ServicesContext';
import { useAuth } from '../../contexts/AuthContext';
import { OrderNote } from '../../models/OrderNote';
import { SaveOrderNoteInput } from '../../services/OrderNotes/SaveOrderNote/SaveOrderNoteInput';
import { Button } from '../../common-components/Button/Button';
import { CardOrderNote } from './CardOrderNote/CardOrderNote';
import { FormOrderNote, FormOrderNoteValues } from './FormOrderNote/FormOrderNote';
import { FooterContent } from './styles';

interface IModalOrderNotesProps {
    visible: boolean;
    close: (changed?: boolean) => void;
    title: string;
    orderId?: number;
    notList?: boolean;
}

const ModalOrderNotes: React.FC<IModalOrderNotesProps> = ({
    visible,
    close,
    title,
    orderId,
    notList
}) => {

    const { session } = useAuth();
    const [ notes, setNotes ] = React.useState<OrderNote[]>([]);
    const [ formVisible, setFormVisible ] = React.useState<boolean>(false);
    const [ formValues, setFormValues ] = React.useState<FormOrderNoteValues|undefined>(undefined);

    const {
        getOrderNotesByOrderService,
        saveOrderNoteService,
        toastService
    } = React.useContext(ServicesContext);

    React.useEffect(() => {
        if (!visible) return;
        getNotes();
    }, [ visible, notList ]);

    const body = React.useMemo(() => {
        if (!visible) return null;

        if (formVisible || notList) {
            return (
                <FormOrderNote
                    values={formValues}
                    change={(values) => setFormValues(values)}
                    notLabel={notList}
                />
            );
        }

        const orderHasAnyNotes = notes?.length > 0;

        if (!orderHasAnyNotes) {
            return (
                <Alert variant='warning'>
                    {strings.NoneNoteFound}
                </Alert>
            );
        }

        return notes.map(note => {
            return (
                <CardOrderNote key={`order-note-${note.id}`} note={note} />
            )
        });
    }, [ visible, formVisible, notes, formValues, notList ]);

    const footer = React.useMemo(() => {
        if (!visible) return null;

        if (formVisible || notList) {
            return (
                <>
                    <Button text={strings.Cancel} width='190px' marginRight='20px' click={() => cancelForm()} />
                    <Button text={strings.ConfirmNote} width='190px' click={() => confirmNote()} />
                </>
            );
        }

        return (
            <>
                <Button text={strings.NewNote} width='190px' marginRight='20px' click={() => insertNote()} />
                <Button text={strings.Update} width='190px' click={() => getNotes()} />
            </>
        );
    }, [ visible, formVisible, notes, formValues, notList ]);

    if (!visible) return null;

    return (
        <Modal
            show={visible}
            onHide={() => clickCloseModal()}
            backdrop="static"
            keyboard={false}
            size="lg"
        >
            <Modal.Header closeButton style={{padding:'10px 1rem', height:'50px'}}>
                <Modal.Title>
                    {title}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body id='modalOrderNotesBody' style={{maxHeight:'78vh',overflowY:'auto'}}>
                {body}
            </Modal.Body>

            <Modal.Footer>
                <FooterContent>
                    {footer}
                </FooterContent>
            </Modal.Footer>
        </Modal>
    );

    function getNotes() {
        if (!orderId || notList) return;
        getOrderNotesByOrderService
            .handle({orderId: orderId}).then(result => {
                setNotes(result?.value?.notes || []);
            })
            .catch(() => {
                toastService.warning(strings.OpsSomethingWrongGettingTheData);
            });
    }

    function clickCloseModal(changed?: boolean) {
        setFormVisible(false);
        setFormValues(undefined);
        close(changed);
    }

    function insertNote() {
        setFormValues(undefined);
        setFormVisible(true);
    }

    function cancelForm() {
        setFormVisible(false);
        setFormValues(undefined);
        if (notList) {
            clickCloseModal();
        }
    }

    function confirmNote() {
        if (!orderId) return;

        if (!formValues?.value) {
            toastService.warning(strings.EnterOrderNote);
            return;
        }

        const input = new SaveOrderNoteInput();
        input.orderId = orderId;
        input.value = formValues.value;
        input.createdById = session?.user?.id;
        saveOrderNoteService.handle(input)
            .then(result => {
                if (notList) {
                    setFormValues(undefined);
                    clickCloseModal(true);
                    return;
                }
                cancelForm();
                getNotes();
            })
            .catch(() => {
                toastService.warning(strings.OpsSomethingWrongSendTheData);
            });
    }
}

export {ModalOrderNotes};